import React, { useState, useEffect } from "react";
import Navbar from "./Navbar";
import Footer from "./Footer";
import { db, storage } from "../firebase";
import { ref as dbRef, onValue, update, remove } from "firebase/database";
import { ref as storageRef, uploadBytesResumable, getDownloadURL, deleteObject } from "firebase/storage";
import { Edit, ArrowLeft, Upload, AlertCircle, ChevronRight, FileText, Eye, CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { toast } from "react-toastify";

const DocumentList = () => {
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState({});
  const [currentFile, setCurrentFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [userInfo, setUserInfo] = useState(null);

  const documents = [
    { id: 1, name: "शासनमान्य संस्थेचे प्रमाणपत्र", englishName: "Accredited Certificate" },
    { id: 2, name: " शासनमान्य संस्थेचे शेवटच्या वर्षाचे गुणपत्रक.", englishName: "Statutory Institute Marksheet" },
    { id: 3, name: "अधिवस प्रमाणपत्र", englishName: "Domicile Certificate" },
    { id: 4, name: "नाव पत्ता असलेल्या कागदावर स्वतःचा फोटो लावून फोटो स्वसाक्षांकित करावा", englishName: "Self Attested Photo Document" },
    { id: 5, name: "आय कार्ड साईझ फोटो", englishName: "ID Card Size Photo" },
    { id: 6, name: "अर्जदाराची स्वाक्षरी", englishName: "Applicant Signature" },
    { id: 7, name: "आधार कार्ड", englishName: "Aadhar Card" },
    { id: 8, name: "पॅन कार्ड", englishName: "PAN Card" },
    { id: 9, name: "प्रतिज्ञा पत्र", englishName: "Affidavit" },
    { id: 10, name: "रोजगार कार्ड", englishName: "Employment Card" },
    { id: 11, name: "DSC टोकन", englishName: "DSC Token" },
    { id: 12, name: "Other Document", englishName: "Other Document" },
  ];

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('currentUser'));
    if (user) {
      setUserInfo(user);
      // Standardized path: ekamwatapzp/users/{key}/documents
      const docsRef = dbRef(db, `ekamwatapzp/users/${user._key}/documents`);
      
      const unsubscribe = onValue(docsRef, (snapshot) => {
        if (snapshot.exists()) {
          setUploadedFiles(snapshot.val());
        } else {
          setUploadedFiles({});
        }
        setLoading(false);
      });
      return () => unsubscribe();
    } else {
      setLoading(false);
    }
  }, []);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error("फाईल 2MB पेक्षा लहान असावी.");
        return;
      }
      setCurrentFile(file);
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!currentFile || !userInfo) return;

    setUploading(true);
    setUploadProgress(0);

    try {
      const fileExt = currentFile.name.split('.').pop();
      const fileName = `${selectedDoc.id}_${Date.now()}.${fileExt}`;
      const fileRef = storageRef(storage, `ekamwatapzp/documents/${userInfo._key}/${fileName}`);
      
      const uploadTask = uploadBytesResumable(fileRef, currentFile);

      uploadTask.on(
        "state_changed",
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setUploadProgress(Math.round(progress));
        },
        (error) => {
          toast.error("अपलोड अयशस्वी झाले.");
          setUploading(false);
        },
        async () => {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
          // Standardized path: ekamwatapzp/users/{key}/documents
          const userDocsRef = dbRef(db, `ekamwatapzp/users/${userInfo._key}/documents`);
          const newDocData = {
            [selectedDoc.id]: {
              url: downloadURL,
              storagePath: `ekamwatapzp/documents/${userInfo._key}/${fileName}`,
              name: currentFile.name,
              type: currentFile.type,
              uploadedAt: new Date().toISOString()
            }
          };

          await update(userDocsRef, newDocData);
          toast.success(`${selectedDoc.name} यशस्वीरीत्या अपडेट झाले!`);
          setSelectedDoc(null);
          setCurrentFile(null);
          setPreviewUrl(null);
          setUploading(false);
        }
      );
    } catch (error) {
      toast.error("काहीतरी चूक झाली.");
      setUploading(false);
    }
  };


  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50">
        <Loader2 className="animate-spin text-blue-600 mb-4" size={48} />
        <p className="text-slate-600 font-bold uppercase text-xs tracking-widest">Loading...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-slate-50">
      <Navbar />
      
      <main className="flex-grow py-8 px-4 md:px-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
             <div>
                <h1 className="text-3xl font-extrabold text-blue-900 leading-tight">कागदपत्रांची यादी आणि अपलोड</h1>
                <p className="text-slate-500 font-medium mt-1">आपले सर्व आवश्यक कागदपत्रे येथे तपासा आणि अपलोड करा.</p>
             </div>
             <div className="bg-white px-4 py-2 rounded-xl shadow-sm border border-slate-200 text-sm font-bold text-slate-600 flex items-center gap-2">
                सांगली जिल्हा परिषद <ChevronRight size={16} className="text-slate-400" /> <span className="text-blue-600">कागदपत्रे</span>
             </div>
          </div>

          {!selectedDoc ? (
            <div className="bg-white rounded-2xl shadow-xl border border-blue-100 overflow-hidden">
               <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-blue-50/50 border-b border-blue-100">
                        <th className="px-6 py-5 text-center font-black text-blue-900 text-xs uppercase tracking-widest border-r border-blue-100 w-20">#</th>
                        <th className="px-6 py-5 text-left font-black text-blue-900 text-xs uppercase tracking-widest border-r border-blue-100">कागदपत्रांचे नाव</th>
                        <th className="px-6 py-5 text-center font-black text-blue-900 text-xs uppercase tracking-widest border-r border-blue-100">स्थिती</th>
                        <th className="px-6 py-5 text-center font-black text-blue-900 text-xs uppercase tracking-widest">कृती</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {documents.map((doc) => {
                        const uploaded = uploadedFiles[doc.id];
                        return (
                          <tr key={doc.id} className="hover:bg-slate-50/50 transition-colors">
                            <td className="px-6 py-5 border-r border-slate-100 text-center font-bold text-slate-500">{doc.id}</td>
                            <td className="px-6 py-5 border-r border-slate-100">
                               <div className="flex flex-col">
                                  <span className="font-bold text-slate-800">{doc.name}</span>
                                  <span className="text-[10px] text-slate-400 font-black uppercase tracking-tighter">{doc.englishName}</span>
                               </div>
                            </td>
                            <td className="px-6 py-5 border-r border-slate-100 text-center">
                               {uploaded ? (
                                 <div className="flex flex-col items-center gap-1">
                                    <span className="bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full text-[11px] font-black uppercase tracking-wider flex items-center gap-1">
                                       <CheckCircle2 size={12} /> अपलोड झाले
                                    </span>
                                    <a href={uploaded.url} target="_blank" rel="noopener noreferrer" className="text-blue-500 text-[10px] font-bold underline flex items-center gap-1 mt-1">
                                       <Eye size={10} /> फाईल पहा
                                    </a>
                                 </div>
                               ) : (
                                 <span className="bg-rose-100 text-rose-700 px-3 py-1 rounded-full text-[11px] font-black uppercase tracking-wider flex items-center gap-1 mx-auto w-fit">
                                    <XCircle size={12} /> गहाळ
                                 </span>
                               )}
                            </td>
                            <td className="px-6 py-5">
                               <div className="flex items-center justify-center gap-2">
                                  <button 
                                    onClick={() => setSelectedDoc(doc)}
                                    className={`px-3 py-1.5 rounded-lg font-bold text-[11px] flex items-center gap-1 transition-all ${
                                      uploaded ? 'bg-white border border-blue-100 text-blue-600 hover:bg-blue-50' : 'bg-blue-600 text-white hover:bg-blue-700 shadow-md shadow-blue-100'
                                    }`}
                                  >
                                    <Edit size={14} /> {uploaded ? 'बदला' : 'अपलोड करा'}
                                  </button>
                               </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
               </div>
            </div>
          ) : (
            <div className="bg-white rounded-3xl shadow-2xl border border-blue-100 p-8 max-w-4xl mx-auto animate-in slide-in-from-right-10">
               <button onClick={() => setSelectedDoc(null)} className="flex items-center gap-2 text-slate-400 hover:text-blue-600 font-bold text-xs uppercase tracking-widest mb-8">
                  <ArrowLeft size={16} /> यादीवर परत जा
               </button>

               <div className="space-y-8">
                  <header>
                     <span className="text-blue-600 font-black text-[10px] uppercase tracking-[0.2em] block mb-2">दस्तावेज अपलोड</span>
                     <h2 className="text-2xl font-black text-slate-800 tracking-tight">{selectedDoc.englishName}</h2>
                     <p className="text-slate-500 text-sm mt-1">{selectedDoc.name}</p>
                  </header>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                     <div className="space-y-6">
                        <div className="p-6 bg-slate-50 rounded-2xl border border-dashed border-slate-300">
                           <input 
                             type="file" 
                             accept="application/pdf,image/*" 
                             onChange={handleFileChange}
                             disabled={uploading}
                             className="block w-full text-xs text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-xs file:font-semibold file:bg-blue-600 file:text-white hover:file:bg-blue-700"
                           />
                        </div>

                        {uploading && (
                          <div className="space-y-2">
                             <div className="flex justify-between text-[10px] font-black text-blue-600">
                                <span>अपलोड होत आहे...</span>
                                <span>{uploadProgress}%</span>
                             </div>
                             <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                                <div className="h-full bg-blue-600 transition-all" style={{width: `${uploadProgress}%`}}></div>
                             </div>
                          </div>
                        )}

                        <div className="flex gap-4">
                            <button 
                              onClick={handleUpdate}
                              disabled={!currentFile || uploading}
                              className={`flex-grow py-4 rounded-xl font-black uppercase tracking-widest text-xs shadow-xl transition-all ${
                                !currentFile || uploading ? 'bg-slate-100 text-slate-300' : 'bg-blue-600 text-white hover:bg-blue-700'
                              }`}
                            >
                               {uploading ? 'प्रक्रिया सुरू आहे...' : 'अपलोड करा (Update Now)'}
                            </button>
                        </div>
                     </div>

                     <div className="bg-slate-900 rounded-3xl aspect-square flex flex-col items-center justify-center p-8 text-center border-8 border-slate-50 shadow-inner">
                        {previewUrl ? (
                           <div className="w-full h-full flex items-center justify-center animate-in zoom-in-95 duration-300">
                              {currentFile.type === 'application/pdf' ? (
                                <FileText size={80} className="text-blue-500" />
                              ) : (
                                <img src={previewUrl} alt="Preview" className="max-h-full rounded-lg shadow-2xl" />
                              )}
                           </div>
                        ) : uploadedFiles[selectedDoc.id] ? (
                           <div className="w-full h-full flex flex-col items-center justify-center animate-in fade-in duration-500">
                              {uploadedFiles[selectedDoc.id].type === 'application/pdf' ? (
                                <FileText size={80} className="text-emerald-500" />
                              ) : (
                                <img src={uploadedFiles[selectedDoc.id].url} alt="Uploaded" className="max-h-full rounded-lg shadow-2xl" />
                              )}
                              <span className="text-emerald-500 font-black text-[10px] mt-4 tracking-widest">सध्याची फाईल</span>
                           </div>
                        ) : (
                           <div className="space-y-4">
                              <Upload size={48} className="text-slate-700 mx-auto" />
                              <p className="text-blue-400/50 font-bold italic text-sm">No document found..</p>
                           </div>
                        )}
                     </div>
                  </div>
               </div>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default DocumentList;
